import { run } from './motion-choreography';
import { mapStyles, markerIcon } from './directory-map';

// Shared Google Maps loader
window._loadGoogleMaps = (() => {
  let promise = null;
  return () => {
    if (window.google?.maps) return Promise.resolve();
    if (promise) return promise;
    const key = window.GOOGLE_MAPS_API_KEY;
    if (!key) return Promise.reject('No Google Maps API key');
    promise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}`;
      script.async = true;
      script.onload = resolve;
      document.head.appendChild(script);
    });
    return promise;
  };
})();

// Global stores registered on init
document.addEventListener('alpine:init', () => {
  Alpine.store('theme', {
    current: 'light'
  });

  Alpine.store('menu', {
    isOpen: false,   // drives the mount; held true through the exit choreography
    isActive: false, // logical open/closed state — flips immediately for the trigger

    open() {
      if (this.isActive) return;
      this.isActive = true;
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
      window.lenis?.stop(); // pause smooth scroll while the overlay locks the body
      // Wait for the root to mount, then choreograph four layers: the overlay
      // scrim fades up, the panel drops in behind it, the menu items stagger in on
      // top, and the social icons reveal center-out. Each stagger's fill:both pins
      // its children at opacity 0 from frame 0 and its startDelay holds them there
      // until the background has landed, so they visibly fall into place after the
      // fade rather than during it.
      requestAnimationFrame(() => {
        const root = document.querySelector('.c-mobile-nav-root');
        if (!root) return;
        const items = root.querySelector('.c-mobile-nav__items');
        const social = root.querySelector('.c-mobile-nav__social');
        run('choreography-mobile-menu-enter', root);
        if (items) run('choreography-menu-reveal', items);
        if (social) run('choreography-social-reveal', social);
      });
    },

    close() {
      if (!this.isActive) return;
      this.isActive = false;
      const root = document.querySelector('.c-mobile-nav-root');
      const finish = () => {
        this.isOpen = false;
        document.body.style.overflow = '';
        window.lenis?.start(); // resume smooth scroll once the overlay is dismissed
      };
      if (!root) return finish();
      // Reverse the sequence: the items + social icons sink out (reverse stagger)
      // first, and the panel exit is held slightly (see mobile-menu-exit delays)
      // so that dismissal is visible before the maroon collapses over them. The
      // panel + scrim exit is the visible teardown, so unmount when *it* resolves —
      // the still-dismissing children are masked by the collapsing panel and don't
      // need to be awaited (which is what kept the old close alive an extra ~300ms).
      const items = root.querySelector('.c-mobile-nav__items');
      const social = root.querySelector('.c-mobile-nav__social');
      if (items) run('choreography-list-dismiss', items);
      if (social) run('choreography-social-dismiss', social);
      run('choreography-mobile-menu-exit', root).then(finish);
    },

    toggle() {
      this.isActive ? this.close() : this.open();
    }
  });

  Alpine.store('donate', {
    isOpen: false, // drives the mount; held true through the exit choreography

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
      window.lenis?.stop(); // pause smooth scroll while the overlay locks the body
      // Wait for the root to mount, then run the coordinated reveal: the scrim
      // fades up while the panel slides in from the right (see donate-drawer-enter).
      requestAnimationFrame(() => {
        const root = document.querySelector('.c-donate-drawer-root');
        if (root) run('choreography-donate-drawer-enter', root);
      });
    },

    close() {
      const root = document.querySelector('.c-donate-drawer-root');
      const finish = () => {
        this.isOpen = false;
        document.body.style.overflow = '';
        window.lenis?.start(); // resume smooth scroll once the overlay is dismissed
        // Drop the #donate deep-link fragment so a refresh doesn't re-open it.
        if (window.location.hash === '#donate') {
          history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      };
      if (!root) return finish();
      // Panel slides out first, the scrim fades after it — unmount once that
      // teardown resolves.
      run('choreography-donate-drawer-exit', root).then(finish);
    },

    toggle() {
      this.isOpen ? this.close() : this.open();
    }
  });

  Alpine.store('staffDrawer', {
    isOpen: false, // drives the mount; held true through the exit choreography

    open(postId) {
      htmx.ajax('GET', `/wp-admin/admin-ajax.php?action=get_staff_member&id=${postId}`, {
        target: '#staff-drawer-content'
      });
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
      window.lenis?.stop(); // pause smooth scroll while the overlay locks the body
      // Wait for the root to mount, then run the coordinated reveal: the scrim
      // fades up while the panel slides in from the left (see staff-drawer-enter).
      requestAnimationFrame(() => {
        const root = document.querySelector('.c-staff-drawer-root');
        if (root) run('choreography-staff-drawer-enter', root);
      });
    },

    close() {
      const root = document.querySelector('.c-staff-drawer-root');
      const finish = () => {
        this.isOpen = false;
        document.body.style.overflow = '';
        window.lenis?.start(); // resume smooth scroll once the overlay is dismissed
      };
      if (!root) return finish();
      // Panel slides out first, the scrim fades after it — unmount once that
      // teardown resolves.
      run('choreography-staff-drawer-exit', root).then(finish);
    }
  });

  Alpine.store('directoryModal', {
    isOpen: false,
    postId: null,
    _maps: [],

    open(id) {
      this.postId = id;
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
      window.lenis?.stop(); // pause smooth scroll while the overlay locks the body
      this._initMaps();
      // Tier 3 modal choreography — pop the card + fade the scrim once visible.
      requestAnimationFrame(() => {
        const root = document.querySelector('.c-directory-modal-root');
        if (root) run('choreography-modal-enter', root);
      });
    },

    close() {
      const root = document.querySelector('.c-directory-modal-root');
      const finish = () => {
        this.isOpen = false;
        this.postId = null;
        document.body.style.overflow = '';
        window.lenis?.start(); // resume smooth scroll once the overlay is dismissed
        this._maps = [];
      };
      // Play the exit choreography, then tear down once it resolves.
      if (root) {
        run('choreography-modal-exit', root).then(finish);
      } else {
        finish();
      }
    },

    async _initMaps() {
      // Ensure Google Maps API is loaded
      try {
        await window._loadGoogleMaps();
      } catch { return; }

      // Wait for Alpine to render the active modal item
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const modal = document.querySelector('.c-directory-modal');
      if (!modal) return;

      const mapEls = modal.querySelectorAll('.c-directory-modal__map[data-lat]');
      mapEls.forEach((el) => {
        if (el.dataset.mapInit) return;

        const lat = parseFloat(el.dataset.lat);
        const lng = parseFloat(el.dataset.lng);
        if (isNaN(lat) || isNaN(lng)) return;

        const map = new google.maps.Map(el, {
          center: { lat, lng },
          zoom: 16,
          disableDefaultUI: true,
          zoomControl: true,
          styles: mapStyles(),
        });

        // The focused item's category pin, in the selected (inverted) state.
        new google.maps.Marker({
          position: { lat, lng },
          map,
          title: el.dataset.label || '',
          icon: markerIcon({
            glyph: el.dataset.catIcon || null,
            colorToken: el.dataset.catColor || null,
            type: el.dataset.type || null,
            active: true,
          }),
        });

        el.dataset.mapInit = 'true';
        this._maps.push(map);
      });
    },
  });

  // Generic loading-state registry for the morphing brand loader (see
  // loader.twig / loaderMorph). Any region flips its own keyed loader on/off, so
  // multiple areas — mobile map, event filtering, staff drawer — can load
  // independently. Reassigning `_active` wholesale keeps Alpine's reactivity
  // firing for the loader's x-effect / :class consumers.
  Alpine.store('loader', {
    _active: {},

    isActive(key = 'default') {
      return !!this._active[key];
    },

    show(key = 'default') {
      if (this._active[key]) return;
      this._active = { ...this._active, [key]: true };
    },

    hide(key = 'default') {
      if (!this._active[key]) return;
      this._active = { ...this._active, [key]: false };
    },

    toggle(key = 'default', force) {
      const next = force === undefined ? !this._active[key] : !!force;
      this._active = { ...this._active, [key]: next };
    },

    // Ergonomic wrapper: show the loader for the lifetime of an async task, and
    // always hide it afterward — even if the task throws.
    async during(key, task) {
      this.show(key);
      try {
        return await task();
      } finally {
        this.hide(key);
      }
    },
  });
});

// Intercept #donate links to toggle the donate drawer
document.addEventListener('click', (e) => {
  const link = e.target.closest('a[href="#donate"]');
  if (!link) return;

  e.preventDefault();
  Alpine.store('donate').toggle();
});

// Open the donate drawer from a #donate deep-link — on initial load (a shared
// `/#donate` or `/donate` redirect) and on any in-page hash change. Uses open()
// rather than toggle() so re-arriving at the fragment never closes it.
const openDonateFromHash = () => {
  if (window.location.hash === '#donate') Alpine.store('donate')?.open();
};

document.addEventListener('alpine:initialized', openDonateFromHash);
window.addEventListener('hashchange', openDonateFromHash);
